import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MdCheck } from 'react-icons/md'
import { useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'

import { IFindCaminhaoByPlaca } from 'modules/caminhao/domain/usecases/FindCaminhaoByPlaca'
import { ICancelaViagem } from 'modules/lotes/domain/usecases/CancelaViagem'
import { IDirecionarCargas } from 'modules/lotes/domain/usecases/DirecionarCargas'
import { IFinalizaViagem } from 'modules/lotes/domain/usecases/FinalizaViagem'
import { IValidateCaminhao } from 'modules/lotes/domain/usecases/ValidateCaminhao'
import { IValidateMotorista } from 'modules/lotes/domain/usecases/ValidateMotorista'
import { useDirecionaCarga } from 'modules/lotes/hooks/direcionaCarga'
import { IFindByCpfNomeFrotistaId } from 'modules/motorista/domain/usecases/FindByCpfNomeFrotistaId'
import { IFindAll } from 'modules/pedagio/domain/usecases/FindAll'
import { IFindActiveTag } from 'modules/pedagio/domain/usecases/FindActiveTag'
import { IsaveTag } from 'modules/pedagio/domain/usecases/SaveTag'
import { IValidateAntt } from 'modules/user/domain/usecases/ValidateAnttImpl'
import Button from 'shared/components/Button'
import ContainerPage from 'shared/components/ContainerPageNew'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import Modal from 'shared/components/Modal'
import SnackBar from 'shared/components/SnackBar'
import { colors } from 'shared/styles/colors'
import ModalAnttRecusada from 'shared/components/ModalAnttRecusada'

import SelecionaCaminhao from './SelecionaCaminhao'
import SelecionaMotorista from './SelecionaMotorista'
import { StyledCheck } from './styles'
import ModalTagPedagio from './components/ModalTagPedagio'

type TProps = {
  findByNome: IFindByCpfNomeFrotistaId
  findByPlaca: IFindCaminhaoByPlaca
  direcionarCargas: IDirecionarCargas
  validateMotorista: IValidateMotorista
  validateCaminhao: IValidateCaminhao
  finalizaViagem: IFinalizaViagem
  cancelaViagem: ICancelaViagem
  validateAntt: IValidateAntt
  findAll: IFindAll
  findActiveTag: IFindActiveTag
  saveTag: IsaveTag
}

const numberOfSteps =
  Object.keys({
    SelecionaMotorista: 0,
    SelecionaCaminhao: 1,
  }).length - 1

const DirecionaCarga: React.FC<TProps> = ({
  findByNome,
  findByPlaca,
  direcionarCargas,
  validateMotorista,
  validateCaminhao,
  finalizaViagem,
  cancelaViagem,
  validateAntt,
  findAll,
  findActiveTag,
  saveTag,
}) => {
  const [step, setStep] = useState(0)
  const [openModal, setOpenModal] = useState(false)
  const [openModalAntt, setOpenModalAntt] = useState(false)
  const [openModalTagPedagio, setOpenModalTagPedagio] = useState(false)
  const {
    motorista,
    caminhao,
    lote,
    cidadeOrigem,
    cidadeDestino,
    setMotorista,
    setCaminhao,
    contaPendencias,
  } = useDirecionaCarga()
  const [habilitaBotaoProximo, setHabilitaBotaoProximo] = useState(false)
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState('')
  const [modalAnttParams, setModalAnttParams] = useState({
    message: '',
    bloquearAnttNaoValida: true,
  })
  const [direcionarCarga, setDirecionarCarga] = useState(false)
  const [direcionarPedagio, setDirecionarPedagio] = useState(true)
  const [operadorasPedagio, setOperadorasPedagio] = useState<string[]>([])
  const history = useHistory()

  const componentSteps = useMemo(() => {
    return [
      {
        component: (
          <SelecionaMotorista
            findByNome={findByNome}
            validateMotorista={validateMotorista}
            finalizaViagem={finalizaViagem}
            cancelaViagem={cancelaViagem}
          />
        ),
      },
      {
        component: (
          <SelecionaCaminhao
            findByPlaca={findByPlaca}
            validateCaminhao={validateCaminhao}
            finalizaViagem={finalizaViagem}
            cancelaViagem={cancelaViagem}
          />
        ),
      },
    ]
  }, [findByPlaca, findByNome, validateMotorista, validateCaminhao])

  const handleNextButton = useCallback(() => {
    setStep(step => {
      const newStep = step + 1
      if (newStep > numberOfSteps) {
        return step
      }
      return newStep
    })
  }, [])

  const handleDirecionarCargas = useCallback(async () => {
    try {
      if (direcionarPedagio && operadorasPedagio.length > 0) {
        setOpenModalTagPedagio(true)
        return
      }
      setLoadingBackdrop(true)
      const cargas = {
        lote_id: lote.id,
        tipo_origem: 7,
        cpf: motorista?.cpf,
        placa: caminhao?.veiculoCavalo?.placa,
        motorista_id: motorista.id,
        caminhao_id: caminhao.id,
        destino: cidadeDestino,
        origem: cidadeOrigem,
        destino_completo: `${lote.destino} - ${cidadeDestino}`,
        origem_completa: `${lote.origem} - ${cidadeOrigem}`,
        valor: lote.valor,
        produto: lote.produto,
        especie: lote.especie,
        pedagio: lote.pedagio,
        filial: lote.filial,
        observacao: lote.observacoes,
        total_pedagio: lote.total_pedagio,
      }
      await direcionarCargas.execute(cargas)
      setOpenModal(true)
    } catch (error: unknown) {
      setOpen(true)
      if (error instanceof Error) {
        setMessages(`Não foi possível concluir a operação. ${error.message}`)
      } else {
        setMessages('Erro ao tentar direcionar a carga')
      }
    } finally {
      setLoadingBackdrop(false)
    }
  }, [motorista, caminhao, lote, direcionarPedagio])

  const handleRegister = async () => {
    try {
      setLoadingBackdrop(true)

      const anttResponse = await validateAntt.execute({
        cnpj: caminhao?.frotista?.cnpj,
        antt: caminhao?.frotista?.antt,
      })
      if (!anttResponse?.aptoTransporte) {
        setModalAnttParams({
          message: anttResponse.mensagem,
          bloquearAnttNaoValida: anttResponse?.bloquearAnttNaoValida,
        })
        setOpenModalAntt(true)
      } else {
        await handleDirecionarCargas()
      }
    } catch (error: unknown) {
      setOpen(true)
      if (error instanceof Error) {
        setMessages(`Não foi possível concluir a operação. ${error.message}`)
      } else {
        setMessages('Erro ao tentar direcionar a carga')
      }
    } finally {
      setLoadingBackdrop(false)
    }
  }

  const handleGetOperadorasPedagio = async () => {
    try {
      if (lote.pedagio === 'Sem pedágio') return
      const response = await findAll.execute()
      setOperadorasPedagio(response.operadoras)
    } catch (error) {
      setOperadorasPedagio([])
    }
  }

  const handleSetDirecionarCargaModalAntt = () => {
    setDirecionarCarga(true)
  }

  const handleSetDirecionarCargaModalTagPedagio = () => {
    setDirecionarPedagio(false)
  }

  const handleBackButton = useCallback(() => {
    setStep(step => {
      const newStep = step - 1
      if (newStep < 0) {
        history.push('/lotes')
      }
      return step - 1
    })
  }, [])

  useEffect(() => {
    setMotorista(null)
    setCaminhao(null)
    handleGetOperadorasPedagio()
    if (!lote) {
      history.push('/lotes')
    }
  }, [])

  useEffect(() => {
    setHabilitaBotaoProximo(false)
    if (step === 0 && motorista?.nome) {
      setHabilitaBotaoProximo(true)
    }
    if (step === 1 && caminhao?.descricao && !contaPendencias) {
      setHabilitaBotaoProximo(true)
    }
  }, [habilitaBotaoProximo, motorista, step, caminhao, contaPendencias])

  useEffect(() => {
    direcionarCarga && handleDirecionarCargas()
  }, [direcionarCarga])

  useEffect(() => {
    !direcionarPedagio && handleDirecionarCargas()
  }, [direcionarPedagio])

  const modalConclusao = () => {
    return (
      <Modal
        openModal={openModal}
        showCloseButton={true}
        size="medium"
        onCloseModal={() => {
          setOpenModal(false)
          history.push('/lotes-solicitados')
        }}
      >
        <Grid container spacing={3}>
          <Grid item md={12}>
            <Grid container justifyContent={'center'}>
              <StyledCheck />
            </Grid>
          </Grid>
          <Grid item md={12} style={{ fontWeight: 700, color: colors.blue }}>
            <Grid container justifyContent={'center'}>
              Solicitação de carga efetuada com sucesso!
            </Grid>
          </Grid>

          <Grid item md={12}>
            <Grid container justifyContent={'center'}>
              Em instantes, seu motorista receberá esta carga no aplicativo.
            </Grid>
          </Grid>

          <Grid item md={12}>
            <Grid container justifyContent={'center'}>
              <span style={{ textAlign: 'center' }}>
                Você também poderá fazer download da ordem de carregamento no
                menu:
              </span>
              <span style={{ fontWeight: 700, paddingTop: 10 }}>
                Ofertas de cargas {'>'} Cargas solicitadas
              </span>
            </Grid>
          </Grid>

          <Grid item xs={12} md={12}>
            <Grid container justifyContent={'center'}>
              <Button
                onClick={() => {
                  setOpenModal(false)
                  history.push('/lotes-solicitados')
                }}
                color={colors.orange}
                style={{ width: 250 }}
              >
                <MdCheck />
                Concluir
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
    )
  }

  return (
    <ContainerPage
      breadCrumb={
        step >= numberOfSteps ? 'Selecionar caminhão' : 'Selecionar motorista'
      }
      showFooterScaffold
      onClickBackButton={handleBackButton}
      onClickNextButton={
        step >= numberOfSteps ? handleRegister : handleNextButton
      }
      isNextButtonDisabled={!habilitaBotaoProximo}
      titleNextButton={step >= numberOfSteps ? 'Salvar' : 'Próximo'}
      titleBackButton="Voltar"
      colorNextButton={`${habilitaBotaoProximo ? colors.green : colors.gray}`}
      title="Direcionar oferta de carga"
    >
      {componentSteps[step].component}

      {loadingBackdrop ? (
        <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
      ) : (
        <SnackBar
          duration={15000}
          open={open}
          message={messages}
          setOpen={setOpen}
          severity={'error'}
        />
      )}
      {modalConclusao()}
      <ModalAnttRecusada
        setOpenModal={setOpenModalAntt}
        openModal={openModalAntt}
        onConfirm={handleSetDirecionarCargaModalAntt}
        params={modalAnttParams}
      />
      <ModalTagPedagio
        cpfMotorista={motorista?.cpf}
        operadoras={operadorasPedagio}
        findActiveTag={findActiveTag}
        saveTag={saveTag}
        setOpenModal={setOpenModalTagPedagio}
        openModal={openModalTagPedagio}
        onConfirm={handleSetDirecionarCargaModalTagPedagio}
      />
    </ContainerPage>
  )
}

export default DirecionaCarga
